import { create } from "zustand";
import {
  deleteData,
  getData,
  postData,
  putData,
} from "../../../../lib/utils/formHelpers";
import {
  CalibrationTritioResponse,
  IFormNewReport,
  ReportType,
} from "../../../types/report.types";
import { errorAlert, successAlert } from "../../../utils/alerts";
import { IFilterCalibrationAnalysis } from "@app/types/calibration.types";
import { filter } from "./helpers";

type useCalibrationType = {
  calibrationList: CalibrationTritioResponse[];
  get: (
    id: CalibrationTritioResponse["id"]
  ) => CalibrationTritioResponse | undefined;
  save: (report: IFormNewReport) => Promise<boolean>;
  remove: (id: ReportType["id"]) => Promise<void>;
  fetchAll: () => void;
  loading: boolean;
  loadingEdit: boolean;
  loadingSave: boolean;
  setQuery: (query: IFilterCalibrationAnalysis) => void;
  query: IFilterCalibrationAnalysis;
  calibrationTritium: CalibrationTritioResponse | undefined;
  calibrationTritiumFiltered: CalibrationTritioResponse[] | undefined;
  saveCalibrationTritium: (
    calibration: CalibrationTritioResponse,
    idCalibration?: number
  ) => Promise<{ success: boolean; data: { id: number } }>;
  fechtCalibrationTritium: (id: number) => void;
  clearCalibrationTritium: () => void;
  filteredCalibration: () => CalibrationTritioResponse[];
  fechtCalibrationTritiumByAnalysisType: (id: number) => Promise<void>;
};

export const useCalibrationTritium = create<useCalibrationType>((set, get) => ({
  calibrationList: [],
  loading: false,
  loadingEdit: false,
  loadingSave: false,
  calibrationTritium: undefined,
  calibrationTritiumFiltered: undefined,
  query: {},
  setQuery: (query) => set({ query }),
  filteredCalibration: () => filter(get().query, get().calibrationList),
  clearCalibrationTritium: () => set({ calibrationTritium: undefined }),
  get: (id) =>
    get().calibrationList.find((calibration) => calibration.id === id),
  fetchAll: async () => {
    set({ loading: true, calibrationTritium: undefined });
    try {
      const endpoint = "/generic/CalibrationTritium";
      const { data: response } = await getData(endpoint);

      if (response) {
        set({
          calibrationList: response.sort((a, b) => {
            return b.id - a.id;
          }),
        });
      }
    } catch (error) {
      console.error("Error fetching entities:", error);
    } finally {
      set({ loading: false });
    }
  },

  save: async (report: Partial<ReportType>) => {
    set({ loadingSave: true });

    try {
      const isNew = !report?.id;
      const endpoint = `/calibration/${isNew ? "create" : report.id + "/edit"}`;

      const response = isNew
        ? await postData(endpoint, report)
        : await putData(endpoint, report);
      successAlert("Cambios guardados correctamente");
      return response.success;
    } catch (error) {
      errorAlert("Error al guardar los cambios");
      return false;
    } finally {
      set({ loadingSave: false });
      get().fetchAll();
    }
  },

  remove: async (id: ReportType["id"]) => {
    try {
      set({ loading: true });
      const endpoint = `/generic/CalibrationTritium/${id}`;

      await deleteData(endpoint);
      successAlert("Calibración eliminada correctamente");
    } catch (error) {
      errorAlert("Error al eliminar la calibración");
    } finally {
      set({ loading: false });
      get().fetchAll();
    }
  },
  saveCalibrationTritium: async (
    calibration: CalibrationTritioResponse,
    idCalibration?: number
  ) => {
    set({ loadingSave: true });
    try {
      const isNew = !idCalibration;
      const endpoint = `/generic/CalibrationTritium`;

      const response = isNew
        ? await postData(endpoint, calibration)
        : await putData(`${endpoint}/${idCalibration}`, calibration);
      successAlert("Cambios guardados correctamente");
      return { success: true, data: { id: response.data.id } };
    } catch (error) {
      errorAlert("Error al guardar los cambios");
      return { success: false, data: { id: 0 } };
    } finally {
      set({ loadingSave: false });
      get().fetchAll();
    }
  },
  fechtCalibrationTritium: async (id: number) => {
    set({ loadingEdit: true, calibrationTritium: undefined });
    try {
      const endpoint = `/generic/CalibrationTritium/filtered?id=${id}`;
      const { data: response } = await getData(endpoint);

      if (response.length > 0) {
        set({
          calibrationTritium: response[0],
        });
      } else {
        errorAlert("No se encontró la calibración");
        setTimeout(() => {
          window.location.href = "/admin/calibraciones";
        }, 1000);
      }
    } catch (error) {
      console.error("Error fetching entities:", error);
    } finally {
      set({ loadingEdit: false });
    }
  },
  fechtCalibrationTritiumByAnalysisType: async (id: number) => {
    set({ loading: true, calibrationTritium: undefined });
    try {
      const endpoint = `/generic/CalibrationTritium/filtered?analysis_type=${id}`;
      const { data: response } = await getData(endpoint);

      if (response) {
        set({
          calibrationTritiumFiltered: response.sort((a, b) => {
            return b.id - a.id;
          }),
        });
      }
    } catch (error) {
      console.error("Error fetching entities:", error);
    } finally {
      set({ loading: false });
    }
  },
}));
